import { useLabels, type LabelDomain } from '@/lib/labels'

import { ReportPage, type ReportDefinition } from './ReportPage'

/** The labels list for a domain, in the server's order. */
function codes(labels: ReturnType<typeof useLabels>['data'], domain: LabelDomain) {
  return Object.entries(labels?.[domain] ?? {}).map(([value, label]) => ({ value, label }))
}

const PTP: ReportDefinition = {
  key: 'ptp',
  title: 'PTP MIS',
  description: 'One row per promise: what was promised, what came in against it, and whether it was kept.',
  dateFields: [
    { value: 'PTP_DATE', label: 'PTP Date', future: true },
    { value: 'CREATE_DATE', label: 'PTP Create Date' },
  ],
  people: { param: 'agent', label: 'Created by' },
  chips: [
    {
      param: 'status',
      label: 'PTP Status',
      options: [
        { value: 'PENDING', label: 'Pending' },
        { value: 'KEPT', label: 'Kept' },
        { value: 'PARTIALLY_KEPT', label: 'Partially Kept' },
        { value: 'BROKEN', label: 'Broken' },
      ],
    },
    {
      param: 'dpdBucket',
      label: 'DPD bucket',
      options: [
        { value: 'ZERO', label: '0' },
        { value: 'D1_30', label: '1–30' },
        { value: 'D31_60', label: '31–60' },
        { value: 'D61_90', label: '61–90' },
        { value: 'D90_PLUS', label: '90+' },
      ],
    },
  ],
}

export function PtpMisPage() {
  return <ReportPage def={PTP} />
}

export function VisitMisPage() {
  // Visit types and outcomes are the app's own words, from /v1/labels.
  const { data: labels } = useLabels()
  const def: ReportDefinition = {
    key: 'visits',
    title: 'Visit MIS',
    description: 'One row per visit: who went, when, where, what happened and what was collected.',
    dateFields: [
      { value: 'START_DATE', label: 'Visit Start Date' },
      { value: 'PLANNED_DATE', label: 'Visit Planned Date' },
    ],
    people: { param: 'agent', label: 'Agent' },
    chips: [
      { param: 'type', label: 'Visit Type', options: codes(labels, 'visitReason') },
      {
        param: 'status',
        label: 'Visit Status',
        options: [
          { value: 'COMPLETE', label: 'Complete' },
          { value: 'INCOMPLETE', label: 'Incomplete' },
        ],
      },
      { param: 'outcome', label: 'Visit Outcome', options: codes(labels, 'visitOutcome') },
      {
        param: 'ptpReceived',
        label: 'PTP Received',
        single: true,
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
    ],
  }
  return <ReportPage def={def} />
}

const ATTENDANCE: ReportDefinition = {
  key: 'attendance',
  title: 'Attendance MIS',
  description:
    'One row per person per day they signed in: first sign-in, last action, hours, distance between visits and complete visits.',
  people: { param: 'employee', label: 'Employee' },
  chips: [],
  designations: true,
}

export function AttendanceMisPage() {
  return <ReportPage def={ATTENDANCE} />
}
